'use strict';

import extend from 'lodash/extend';
import noop from 'lodash/noop';

/* global jQuery */

/**
 * Low-level communication handler for the File service.
 * Uses Q-based promises for all operations.
 */

(function ($) {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.service = window.atex.aceui.service || {};

  /**
   * Creates a new FileService object.
   *
   * Argument 'fileServiceUrl' - The URL to the file service in the Content REST API.
   * Argument 'serviceSettings' - An object containing settings for this service.
   *
   * The 'serviceSettings' object should look like the following:
   *
   * {
   *   customQ: customQObject
   * }
   *
   * Property 'customQ' is an implementation of a promise based Q library,
   * i.e Angular $q. Defaults to a global Q.
   *
   * Returns a new FileService object.
   */
  atex.aceui.service.FileService = function (fileServiceUrl, serviceSettings) {
    var settings = extend({}, serviceSettings);
    var q = settings.customQ || window.Q;

    return {
      /**
       * Retrieves an ACE content file from the file service and returns the contents as a Blob.
       *
       * @param {Object} transferInfo - a transfer info object containing request details
       * @param {string} transferInfo.fileUri - the file service URI of the file to retrieve
       * @param {function} transferInfo.onProgress - a callback function that, if specified, will receive file on-progress calls
       * @param {function} transferInfo.abort - a transfer abort function added to the input object by this method
       * @param {string} transferInfo.authToken - the authorization token to use for the file request
       *
       * @returns {Promise.<Blob, atex.aceui.error.Error>}
       */
      getFile: function (transferInfo) {
        var deferred = q.defer();

        if (!atex.aceui.FileServiceUtils.isValidUri(transferInfo.fileUri)) {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.FileService',
            cause: 'Not a valid File Service URI: ' + transferInfo.fileUri,
            type: atex.aceui.error.types.INVALID_PARAMETERS
          });

          return q.reject(error);
        }

        var xhr = $.ajaxSettings.xhr();

        var uri = atex.aceui.FileServiceUtils.parseUri(transferInfo.fileUri);
        var fullUrl = fileServiceUrl + '/' + uri.space + (uri.owner !== undefined ? '/' + uri.owner : '') + (uri.path !== undefined ? uri.path : '');

        xhr.open('GET', fullUrl, true);

        xhr.responseType = 'blob';

        if (typeof transferInfo.authToken !== 'undefined') {
          xhr.setRequestHeader('X-Auth-Token', transferInfo.authToken);
        }

        xhr.onload = function () {
          if (this.status === 200) {
            deferred.resolve(xhr.response);
          } else {
            var error = atex.aceui.HTTPUtils.createError(xhr, 'atex.aceui.service.FileService', {
              401: 'Could not authenticate user while reading file from server!',
              403: 'Permission denied while reading file from server!',
              404: 'File was not found in file server!',
              'default': 'There was an error while reading file from server!'
            });

            deferred.reject(error);
          }
        };

        xhr.onerror = function () {
          var error = atex.aceui.HTTPUtils.createError(xhr, 'atex.aceui.service.FileService', {
            401: 'Could not authenticate user while reading file from server!',
            403: 'Permission denied while reading file from server!',
            404: 'File was not found in file server!',
            'default': 'There was an error while reading file from server!'
          });

          deferred.reject(error);
        };

        xhr.ontimeout = function () {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.FileService',
            cause: 'Error while reading file from server: request timed out!',
            type: atex.aceui.error.types.TIMEOUT
          });

          deferred.reject(error);
        };

        xhr.onabort = function () {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.FileService',
            cause: 'Error while reading file from server: request aborted!',
            type: atex.aceui.error.types.OPERATION_CANCELLED
          });

          deferred.reject(error);
        };

        transferInfo.abort = xhr.abort;
        xhr.upload.onprogress = transferInfo.onProgress || noop;

        xhr.send();

        return deferred.promise;
      },

      /**
       * Uploads a File object to the ACE file service in the temporary file space.
       *
       * @param {Object} transferInfo - a transfer info object containing request details
       * @param {string} transferInfo.file - the File object to upload
       * @param {function} transferInfo.onProgress - a callback function that, if specified, will receive file on-progress calls
       * @param {function} transferInfo.abort - a transfer abort function added to the input object by this method
       * @param {string} transferInfo.authToken - the authorization token to use for the file request
       *
       * @typedef {Object} FileInfo - the user data object as returned from the server
       *
       * @typedef {Object} FileUploadRequest - an object containing the result of the file upload request
       * @property {FileInfo} FileUploadRequest.fileInfo - file information on the uploaded file
       * @property {string} FileUploadRequest.location - a file service URI where the uploaded file can be found
       *
       * @returns {Promise.<FileUploadRequest, atex.aceui.error.Error>}
       */
      uploadTemporaryFile: function (transferInfo) {
        var deferred = q.defer();

        var xhr = $.ajaxSettings.xhr();

        var contentType = (typeof transferInfo.file.type !== 'undefined' && transferInfo.file.type.length > 0)
          ? transferInfo.file.type : 'application/octet-stream';

        var fullUrl = fileServiceUrl + '/tmp';

        xhr.open('POST', fullUrl, true);

        xhr.setRequestHeader('Content-Type', contentType);
        xhr.setRequestHeader('Accept', 'application/json');

        if (typeof transferInfo.authToken !== 'undefined') {
          xhr.setRequestHeader('X-Auth-Token', transferInfo.authToken);
        }

        xhr.onload = function () {
          if (this.status === 201) {
            deferred.resolve({location: xhr.getResponseHeader('Location'), fileInfo: JSON.parse(xhr.responseText)});
          } else {
            var error = atex.aceui.HTTPUtils.createError(xhr, 'atex.aceui.service.FileService', {
              401: 'Could not authenticate user while uploading file data to server!',
              403: 'Permission denied while uploading file data to server!',
              'default': 'There was an error while uploading file data to server!'
            });

            deferred.reject(error);
          }
        };

        xhr.onerror = function () {
          var error = atex.aceui.HTTPUtils.createError(xhr, 'atex.aceui.service.FileService', {
            401: 'Could not authenticate user while uploading file data to server!',
            403: 'Permission denied while uploading file data to server!',
            'default': 'There was an error while uploading file data to server!'
          });

          deferred.reject(error);
        };

        xhr.ontimeout = function () {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.FileService',
            cause: 'Error while uploading file data to server: request timed out!',
            type: atex.aceui.error.types.TIMEOUT
          });

          deferred.reject(error);
        };

        xhr.onabort = function () {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.FileService',
            cause: 'Error while uploading file data to server: request aborted!',
            type: atex.aceui.error.types.OPERATION_CANCELLED
          });

          deferred.reject(error);
        };

        transferInfo.abort = xhr.abort;
        xhr.upload.onprogress = transferInfo.onProgress || noop;

        xhr.send(transferInfo.file);

        return deferred.promise;
      }
    };
  };
})(jQuery);
